import type { DiagramMetaExtra } from '@/diagram-persistence/diagram-store';
import { getDiagramStore } from '@/diagram-persistence/store-instance';
import { newCrochetDiagram, newKnittingDiagram } from '@/diagram-persistence/stored-diagram';
import type { StoredDiagram } from '@/diagram-persistence/stored-diagram';
import { getCrochetSizingDefaults } from '@/preferences/crochet-sizing-defaults';
import { getCrochetStrokeDefaults } from '@/preferences/crochet-stroke-defaults';

import { useDocList } from '../doc-gallery/use-doc-list';

/** The active diagrams of one kind. Archived ones are left out: they are
 *  read-only and live in their own list (useArchivedDiagramList). */
export function useDiagramList(kind: 'crochet' | 'knitting') {
    const basePath = kind === 'crochet' ? '/crochet' : '/grid-editor';
    const list = useDocList<StoredDiagram, DiagramMetaExtra>({
        store: getDiagramStore(),
        filter: (m) => m.kind === kind && !m.archived,
        newDoc: (name) => (
            kind === 'crochet'
                ? newCrochetDiagram(name, getCrochetSizingDefaults(), getCrochetStrokeDefaults())
                : newKnittingDiagram(name)
        ),
        hrefFor: (id) => `${basePath}/${id}`,
        skeletonKey: `stitchpress-diagram-count-${kind}`,
        i18nPrefix: 'diagrams',
    });
    // Preserve the public surface existing callers/tests use alongside the
    // generic keys (the page consumes createDoc/renameDoc/deleteDoc directly):
    return { ...list, createDiagram: list.createDoc, renameDiagram: list.renameDoc, deleteDiagram: list.deleteDoc };
}
